import { Address } from "../../../features/address"
import { Data2, useDataTypesHooks } from "../../../features/data-types"
import { O, useFiltersHooks } from "../../../features/ui"
import { Params } from "../../../state/reducers/types"
import { DataType } from "../../../tables"
import { dedupe, firstItem, lastItem } from "../../../utils/listUtils"
import { isDefined, normalize } from "../../../utils/typeUtils"
import { useCreditPaymentLoaders } from "../../administration/loaders/creditPaymentLoaders"
import { useBudgetLoaders } from "../loaders/budgetLoaders"
import { useCustomerLoaders } from "../loaders/customerLoaders"
import { useSaleOperationLoaders } from "../loaders/saleOperationLoaders"
import { ListResponse } from "../../common"
import { PaymentStatus } from "../../operations"
import { useHoardLoaders } from "../loaders/hoardLoaders"
import { useSaleLoaders } from "../loaders/saleLoaders"
import { Customer, CustomerCategory, HoardOrder, SaleOrder } from "../state/types"
import { CreditDebtFilters } from "./types"
import { ValueFilter } from "../../../services/dynamo"
import { useDeliveryLoaders, useDeliveryOrderLoaders } from "../../deliveries"

export const useCustomerHooks = () => {
    const { formatIdentifier, formatPhone } = useDataTypesHooks()
    const { search } = useFiltersHooks()

    const customerLoaders = useCustomerLoaders()
    const budgetLoaders = useBudgetLoaders()
    const creditPaymentLoaders = useCreditPaymentLoaders()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const deliveryLoaders = useDeliveryLoaders()
    const saleOperationLoaders = useSaleOperationLoaders()
    const saleLoaders = useSaleLoaders()
    const hoardLoaders = useHoardLoaders()

    const describeCustomer = (customer: Customer): string => {
        return customer.name + (customer.identifier ? ` (${formatIdentifier(customer.identifier)})` : '')
    }
    
    const describeContact = (customerData: Partial<Customer>): string => {
        const { contactName, contactPhone } = customerData
        const separator = contactName && contactPhone ? ' - ' : ''
        return `${contactName || ''}${separator}${formatPhone(contactPhone) || ''}`
    }

    const canRemoveCustomer = async (customerId: string): Promise<boolean> => {
        const { countBudgets } = budgetLoaders()
        const budgetsCount = await countBudgets('customerId', customerId)

        const { countCreditPayments } = creditPaymentLoaders()
        const creditPaymentsCount = await countCreditPayments('customerId', customerId)

        const { countDeliveryOrders } = deliveryOrderLoaders()
        const deliveryOrdersCount = await countDeliveryOrders('customerId', customerId)

        const { countDeliveries } = deliveryLoaders()
        const deliveriesCount = await countDeliveries('customerId', customerId)

        const { countSaleOperations } = saleOperationLoaders()
        const saleOperationsCount = await countSaleOperations('customerId', customerId)

        return budgetsCount === 0 &&
            creditPaymentsCount === 0 &&
            deliveryOrdersCount === 0 &&
            deliveriesCount === 0 &&
            saleOperationsCount === 0
    }

    const searchCustomers = (
        customers: Customer[],
        text = '',
        showAll = false
    ): Customer[] => {
        const valuesFn = (customerDT: DataType) => {
            const { name, identifier, emails } = customerDT as Customer
            emails
            return [
                normalize(name),
                identifier.toString(),
                ...emails
            ]
        }
        const sortFn = (customerDT_A: DataType, customerDT_B: DataType) => 
            (customerDT_A as Customer).name.localeCompare((customerDT_B as Customer).name)

        return search(customers, valuesFn, sortFn, text, showAll) as Customer[]
    }

    const loadFiltered = async (
        text = '',
        creditAccount?: boolean,
        customerTypeId?: string,
        category?: CustomerCategory
    ) => {
        const { loadAllCustomers } = customerLoaders()
        let customers = await loadAllCustomers(true)
        customers = searchCustomers(customers, text)
        customers = customers.filter(customer => !category || customer.category === category)
        customers = customers.filter(customer => !customerTypeId || customer.customerTypeId === customerTypeId)
        customers = customers.filter(customer => !isDefined(creditAccount) || !!customer.creditAccount)
        return customers.sort((customers_A, customers_B) => customers_A.name.localeCompare(customers_B.name))
    }

    const loadFilteredBatch = async (
        text = '',
        creditAccount?: boolean,
        customerTypeId?: string,
        category?: CustomerCategory,
        startKey?: Params,
        limit = 10
    ): Promise<ListResponse<Customer>> => {
        const customers = await loadFiltered(text, creditAccount, customerTypeId, category)
        const index = startKey ? customers.map(customer => customer.id).indexOf(startKey!.id as string) + 1 : 0
        const batch = customers.slice(index, index + limit)
        const lastKey = lastItem(batch)?.id
        
        return {
            dataTypes: batch,
            lastKey: lastKey ? { id: lastKey } : undefined
        }
    }

    const countFiltered = async (
        text = '',
        creditAccount?: boolean,
        customerTypeId?: string,
        category?: CustomerCategory,
        startKey?: Params
    ): Promise<number> => {
        const customers = await loadFiltered(text, creditAccount, customerTypeId, category)
        const index = startKey ? customers.map(customer => customer.id).indexOf(startKey!.id as string) + 1 : 0
        const nextBatch = customers.slice(index)
        return nextBatch.length
    }
    
    const loadCustomersBatch = async (
        filtersData: Data2 = {},
        startKey?: Params
    ): Promise<{
        response: ListResponse<Customer>
        sales: SaleOrder[]
        hoards: HoardOrder[]
    }> => {
        const { customerId } = filtersData as CreditDebtFilters

        // Todas las ventas/acopios impagos (cuenta corriente y otras formas de pago)
        const sort = {
            index: 'CodeIndex',
            prop: 'code'
        }
        const valueFilters: ValueFilter[] = [
            { prop: 'paymentStatus', value: PaymentStatus.PAYED, equals: false },
            { prop: 'cancelled', value: true, equals: false },
            { prop: 'archived', value: true, equals: false }
        ]
        customerId && valueFilters.push({ prop: 'customerId', value: customerId })

        const { loadSalesBatch } = saleLoaders()
        const salesResponse = await loadSalesBatch({
            startKey,
            sort,
            valueFilters
        })
        const { loadHoardsBatch } = hoardLoaders()
        const hoardsResponse = await loadHoardsBatch({
            startKey,
            sort,
            valueFilters
        })
        
        const saleOperations = [...salesResponse.dataTypes, ...hoardsResponse.dataTypes]
        const customerIds = dedupe(saleOperations.map(operation => operation.customerId))
        const { loadCustomers } = customerLoaders()
        const customers = await loadCustomers(customerIds)

        return {
            response: {
                dataTypes: customers
            },
            sales: salesResponse.dataTypes,
            hoards: hoardsResponse.dataTypes
        }
    }

    return {
        describeCustomer,
        describeContact,
        canRemoveCustomer,
        searchCustomers,
        loadFilteredBatch,
        countFiltered,
        loadCustomersBatch
    }
}

export const customerHelpers = {
    getDeliveryAddress: (customer: Customer): O<Address> => {
        return firstItem(customer.deliveryAddresses)
    }
}
